import { Service } from '@/types/service';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, CheckCircle2, XCircle, AlertCircle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConfidenceBadge } from './ConfidenceBadge';

interface ServiceCardProps {
  service: Service;
  onClick?: () => void;
}

const statusConfig = {
  healthy: {
    icon: CheckCircle2,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    label: 'Operational',
  },
  degraded: {
    icon: AlertCircle,
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    label: 'Degraded',
  },
  down: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    label: 'Down',
  },
  unknown: {
    icon: HelpCircle,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    label: 'Unknown',
  },
};

export const ServiceCard = ({ service, onClick }: ServiceCardProps) => {
  const config = statusConfig[service.status];
  const StatusIcon = config.icon;

  return (
    <Card
      className={cn(
        'p-6 hover:border-primary/50 transition-all cursor-pointer',
        'bg-gradient-to-br from-card to-card/50'
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', config.bg)}>
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{service.name}</h3>
            <p className="text-sm text-muted-foreground">{service.category}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <Badge variant="outline" className={cn('gap-1', config.bg)}>
            <StatusIcon className={cn('h-3 w-3', config.color)} />
            <span className={config.color}>{config.label}</span>
          </Badge>
          {service.confidence && service.checkMethod && (
            <ConfidenceBadge 
              confidence={service.confidence}
              method={service.checkMethod}
              statusDescription={service.statusDescription}
              serviceUrl={service.url}
            />
          )}
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">{service.description}</p>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-mono">{service.url}</span>
        {service.responseTime && (
          <span className="text-primary">{service.responseTime}ms</span>
        )}
      </div>

      {service.lastChecked && (
        <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
          Last checked: {service.lastChecked.toLocaleTimeString()}
        </div>
      )}
    </Card>
  );
};
